<script setup lang="ts">
import { Skeleton } from "@/components/ui/skeleton";
import { TableRow, TableCell } from "@/components/ui/table";

interface SkeletonColumn {
  width?: string;
  type: "text" | "checkbox" | "avatar" | "button" | "combobox";
  size?: "sm" | "md" | "lg";
}

interface Props {
  rows?: number;
  columns: SkeletonColumn[];
}

withDefaults(defineProps<Props>(), {
  rows: 5,
});

function getSkeletonClass(column: SkeletonColumn): string {
  const baseClasses = [];

  if (column.width) {
    baseClasses.push(column.width);
  }

  return baseClasses.join(" ");
}

function getSkeletonSize(column: SkeletonColumn): string {
  if (column.type === "checkbox") return "h-4 w-4";
  if (column.type === "button") return "h-8 w-8";
  if (column.type === "combobox") return "h-8 w-48";

  const sizeClass = column.size === "sm" ? "w-14" : column.size === "lg" ? "w-32" : "w-24";
  return `h-4 ${sizeClass}`;
}
</script>

<template>
  <template v-for="i in rows" :key="`skeleton-${i}`">
    <TableRow>
      <TableCell
        v-for="(column, colIndex) in columns"
        :key="`skeleton-${i}-${colIndex}`"
        :class="getSkeletonClass(column)"
      >
        <Skeleton v-if="column.type === 'checkbox'" :class="getSkeletonSize(column)" />

        <div v-else-if="column.type === 'avatar'" class="flex items-center gap-2">
          <Skeleton class="h-8 w-8 rounded-full" />
          <Skeleton class="h-4 w-20" />
        </div>

        <Skeleton v-else-if="column.type === 'button'" :class="getSkeletonSize(column)" />

        <Skeleton v-else-if="column.type === 'combobox'" :class="getSkeletonSize(column)" />

        <Skeleton v-else :class="getSkeletonSize(column)" />
      </TableCell>
    </TableRow>
  </template>
</template>
